import { useMemo } from "react"
import { useRouter } from "next/router"
import Link from "next/link"

import { capitalize } from "@lib"

type Crumb = {
    path: string,
    label: string
}

const Breadcrumbs: React.FunctionComponent<{
    rootLabel?: string,
    omitRoot?: boolean
    labelOverrides?: Record<string, string>
}> = ({
    omitRoot,
    rootLabel,
    labelOverrides
}) => {
    const router = useRouter()

    const crumbs = useMemo<Crumb[]>(() => {
        // Get path components ignoring query parameters, then convert to Crumbs
        const crumbsFromPath = router.asPath.split(/[#?]/)[0]
            .split("/")
            .map((each, index, all) => {
                const path = all.slice(0, index + 1).join("/")
                return {
                    path: path.length === 0 ? "/" : path,
                    label: path.length === 0 ? (rootLabel || each) : each
                }
            })
        if (omitRoot) {
            return crumbsFromPath.slice(1)
        } else {
            return crumbsFromPath
        }
    }, [router.asPath, rootLabel, omitRoot])

    const handleBreadcrumbItemClick = (index: number) => {
        if (index === crumbs.length - 2) {
            router.replace(`${crumbs[index].path}?${router.query.return ? router.query.return : ""}`)
        } else {
            router.replace(crumbs[index].path)
        }
    }

    return (
        <div id="breadcrumbs" className="text-caption1">
            {crumbs.map((each, index) => {
                const displayLabel = (labelOverrides ? (labelOverrides[each.label.toLowerCase()] || capitalize(each.label)) : capitalize(each.label))

                return (
                    <span key={each.path}>
                        {index > 0 && <span className="mx-[8px]">/</span>}

                        {index === crumbs.length - 1 && <span>{displayLabel}</span>}
                        {index !== crumbs.length - 1 &&
                            <span className="text-primary-900 underline cursor-pointer"
                                onClick={() => handleBreadcrumbItemClick && handleBreadcrumbItemClick(index)}>
                                {displayLabel}
                            </span>
                        }
                    </span>
                )
            })}
        </div>
    )
}

export default Breadcrumbs
